import {memo, useCallback, useState} from 'react';
import styled from 'styled-components';
import {Palette} from 'hubbl-shared';
import {FontConfig} from '../../constants/FontConfig';
import {EventTag} from '../event-tag/EventTag';

interface EventTagsInputProps {
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder: string;
  className?: string;
}

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;

  padding: 12px;
  border-radius: 8px;
  background: ${Palette.primarySubtleGreen};
  min-height: 48px;
  align-items: center;
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 120px;
  border: none;
  background: transparent;
  outline: none;
  font-family: ${FontConfig.Heading5.fontFamily};
  font-size: ${FontConfig.Heading5.fontSize}px;
  font-weight: ${FontConfig.Heading5.fontWeight};
  letter-spacing: ${FontConfig.Heading5.letterSpacing}px;
  color: ${Palette.almostBlack};

  &::placeholder {
    color: ${Palette.almostBlack500};
  }
`;

export const EventTagsInput = memo(
  ({tags, onTagsChange, placeholder, className}: EventTagsInputProps) => {
    const [inputValue, setInputValue] = useState('');

    const handleAddTag = useCallback(() => {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !tags.includes(trimmedValue)) {
        onTagsChange([...tags, trimmedValue]);
        setInputValue('');
      }
    }, [inputValue, tags, onTagsChange]);

    const handleRemoveTag = useCallback(
      (tagToRemove: string) => {
        onTagsChange(tags.filter(tag => tag !== tagToRemove));
      },
      [tags, onTagsChange],
    );

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' || e.key === ',') {
          e.preventDefault();
          handleAddTag();
        } else if (
          e.key === 'Backspace' &&
          inputValue === '' &&
          tags.length > 0
        ) {
          handleRemoveTag(tags[tags.length - 1]);
        }
      },
      [handleAddTag, handleRemoveTag, inputValue, tags],
    );

    return (
      <TagsContainer className={className}>
        {tags.map(tag => (
          <EventTag key={tag} tag={tag} onRemove={() => handleRemoveTag(tag)} />
        ))}
        <TagInput
          type="text"
          placeholder={placeholder}
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleAddTag}
        />
      </TagsContainer>
    );
  },
);
